import * as React from 'react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Search, X, Loader2, UserPlus } from 'lucide-react'
import { useSearchAudienceContacts } from '@/hooks/useWAAudiences'
import type { WAAudienceContactSearchResult } from '@/lib/api-client'

interface ContactPickerProps {
  selectedContacts: WAAudienceContactSearchResult[]
  onContactsChange: (contacts: WAAudienceContactSearchResult[]) => void
  className?: string
}

export function ContactPicker({ selectedContacts, onContactsChange, className }: ContactPickerProps) {
  const [searchQuery, setSearchQuery] = React.useState('')
  const { data, isPending } = useSearchAudienceContacts(searchQuery)

  const searchResults = data?.data || []

  const isSelected = (contactId: number) =>
    selectedContacts.some(c => c.contactId === contactId)

  const handleAdd = (contact: WAAudienceContactSearchResult) => {
    if (!isSelected(contact.contactId)) {
      onContactsChange([...selectedContacts, contact])
    }
  }

  const handleRemove = (contactId: number) => {
    onContactsChange(selectedContacts.filter(c => c.contactId !== contactId))
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Selected contacts */}
      {selectedContacts.length > 0 && (
        <div>
          <p className="text-xs text-text-muted mb-2">
            {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
          </p>
          <div className="flex flex-wrap gap-1.5">
            {selectedContacts.map((contact) => (
              <Badge
                key={contact.contactId}
                variant="secondary"
                className="pl-2 pr-1 py-1 gap-1"
              >
                <span className="text-xs">{contact.patientName}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-4 w-4 p-0 hover:bg-destructive/20 rounded-full"
                  onClick={() => handleRemove(contact.contactId)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
        <Input
          placeholder="Search by name, phone, or PNO..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-9 h-9"
        />
        {isPending && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-text-muted" />
        )}
      </div>

      {/* Search results */}
      {searchQuery.length >= 2 && (
        <ScrollArea className="max-h-[240px]">
          <div className="space-y-1">
            {searchResults.length === 0 && !isPending && (
              <p className="text-sm text-text-muted text-center py-4">
                No contacts found
              </p>
            )}
            {searchResults.map((contact) => {
              const selected = isSelected(contact.contactId)
              return (
                <div
                  key={contact.contactId}
                  className={cn(
                    'flex items-center justify-between p-2 rounded-md text-sm transition-colors',
                    selected
                      ? 'bg-primary/10 border border-primary/20'
                      : 'bg-muted/30 hover:bg-muted/50 cursor-pointer'
                  )}
                  onClick={() => !selected && handleAdd(contact)}
                >
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-text-dark">
                      {contact.patientName}
                    </span>
                    <span className="text-text-muted ml-2 text-xs">
                      {contact.patientPno}
                    </span>
                    <div className="text-xs text-text-muted mt-0.5">
                      {contact.waPhoneNumber}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <Badge variant="outline" className="text-[10px]">
                      {contact.branchName}
                    </Badge>
                    {selected ? (
                      <Badge variant="default" className="text-[10px]">
                        Added
                      </Badge>
                    ) : (
                      <UserPlus className="w-3.5 h-3.5 text-text-muted" />
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </ScrollArea>
      )}

      {searchQuery.length < 2 && selectedContacts.length === 0 && (
        <p className="text-sm text-text-muted text-center py-4">
          Search for contacts by name, phone number, or PNO
        </p>
      )}
    </div>
  )
}
